import { batch, type Signal } from "@preact/signals-react";
import type { RoomDep, RoomConnectionContainer, RoomConnectionStatus } from "../room.types";
import type { RoomStateInternal } from "../roomState/roomState";
import { DEFAULT_HOST_TERM, HOST_PLAYER_ID, HOST_PLAYS_OPTION_ID, normalizeHostPlays, type RoomBroadcast, type GameSavedOptions } from "@merels/room";
import { getBgioInternal } from "../../game/bgio/bgio";
import { derivePlaceholderSeatIndexes } from "../../game/bgio/seating";
import type { Mate } from "@merels/room/types";
import { withReconstructedGameStates } from "../../game/moveHistory/moveHistory";
import { needsMateResync } from "./mateResync";
import { hashGameState } from "@merels/game-logic";
import { RoomClosedError, type CloudRoomProxyEnv } from "../roomProxy/cloudRoomProxy";

export function makeRoomConnection(
  container: RoomConnectionContainer,
  state: RoomStateInternal,
  config: { workerUrl: string; clientId: string; env?: CloudRoomProxyEnv }
): RoomDep["connection"] {
  const { workerUrl, clientId, env } = config;
  const { moveHistory } = container

  // Guards against out-of-order async reconstruction (see
  // updateActiveGameFromState): each call bumps this before doing any
  // awaiting, so an earlier-triggered-but-later-resolving reconstruct()
  // can detect it's stale and discard its result instead of clobbering a
  // newer one.
  let reconstructGeneration = 0;

  const refreshSavedOptions = async (gameId: string) => {
    try {
      const proxy = container.roomProxyManager.currentRoomProxy.value;
      if (!proxy) return;
      const options = await proxy.getSavedGameOptions({ gameId });
      state.savedOptions.value = options;
    } catch (e) {
      console.error("Failed to refresh saved options", e);
    }
  };

  const refreshGameChannelPreference = async (gameId: string) => {
    try {
      const proxy = container.roomProxyManager.currentRoomProxy.value;
      if (!proxy) return;
      const { channel } = await proxy.getGameChannelPreference({ gameId });
      state.gameChannelPreference.value = channel;
    } catch (e) {
      console.error("Failed to refresh game channel preference", e);
    }
  };

  const refreshChallengeConfigurationPreference = async (gameId: string) => {
    try {
      const proxy = container.roomProxyManager.currentRoomProxy.value;
      if (!proxy) return;
      state.challengeConfigurationPreference.value = (await proxy.getChallengeConfigurationPreference({ gameId })).challengeConfiguration;
    } catch (e) {
      console.error("Failed to refresh challenge configuration preference", e);
    }
  };

  const updateActiveGameFromState = (gameData: any) => {
    // Every call — sync or async-reconstructing — invalidates any
    // reconstruction already in flight from an earlier call, regardless of
    // which one's promise happens to resolve first.
    const myGeneration = ++reconstructGeneration;

    if (!gameData) {
      state.activeGame.value = null;
      return;
    }

    let playerToMateIds: string[][] = [];
    if (gameData.mateGroups) {
      playerToMateIds = gameData.mateGroups;
    } else if (gameData.mateSlotMap) {
      const map = gameData.mateSlotMap as Record<string, number>;
      const groups: string[][] = [];
      Object.entries(map).forEach(([mateId, slot]) => {
        if (!groups[slot]) groups[slot] = [];
        groups[slot].push(mateId);
      });
      playerToMateIds = groups;
    }

    if (gameData.gameId === 'new-game') {
      state.activeGame.value = {
        gameId: gameData.gameId,
        gameState: {},
        ctx: {},
        playerToMateIds: [],
        playerIds: ['Creator'],
        channel: gameData.channel,
        gameVersion: gameData.gameVersion,
        gameOptions: gameData.gameOptions,
        challengeConfiguration: gameData.challengeConfiguration,
        managed: gameData.managed,
        actions: gameData.actions,
        initialState: { G: {} },
        seed: gameData.seed,
      };
      return;
    }

    if (gameData.gameVersion) {
      const reconstruct = async () => {
        try {
          const { ProcessGameConfig, InitializeGame, CreateGameReducer } = await getBgioInternal();
          const loadPlugin = container.gameRegistry.loadGamePluginAtVersion
          if (!loadPlugin) throw new Error('Game registry cannot load a pinned game version')
          const plugin = await loadPlugin(gameData.gameId, gameData.gameVersion)

          if (!plugin || !plugin.bgioGameDef) {
            throw new Error(`No game plugin found for game ${gameData.gameId}`);
          }

          const processedGame = ProcessGameConfig({ ...plugin.bgioGameDef, seed: gameData.seed });
          const reducer = CreateGameReducer({ game: processedGame, isClient: false });

          // Always start from a freshly-initialized bgio State — it's the
          // only source of structurally-required fields (plugins, e.g. the
          // seeded `random` plugin's internal state; _undo/_redo/_stateID)
          // that a realtime-game checkpoint (see networkedSim.ts's
          // checkpointState) never has and can't carry through: it only
          // ever knows G, and optionally ctx. Reuse the room-minted seed so
          // every client's independently-computed setup() is identical,
          // instead of each browser picking its own.
          let currentBgioState = InitializeGame({
            game: processedGame,
            numPlayers: gameData.mateGroups?.length ?? 2,
            setupData: {
              gameOptions: gameData.gameOptions,
              challengeConfiguration: gameData.challengeConfiguration,
              managedRoom: Boolean(gameData.managed),
              placeholderSeatIndexes: derivePlaceholderSeatIndexes(gameData.mateIDs ?? []),
            },
          });

          // If the room has moved past initial setup (a checkpoint, or a
          // debug-panel state edit), overlay its G/ctx onto that skeleton.
          // `plugins` etc. stay as freshly-initialized — safe for a
          // realtime game's own moves, which never touch them; a plugin
          // that's actually mid-sequence (e.g. Spacewar!'s `random`, used
          // by `hyperspace`) restarts from the room's seed rather than
          // continuing, but restarts identically for every client, so it
          // can't cause cross-client divergence, only a different draw
          // than uninterrupted play would have produced.
          if (gameData.initialState && Object.keys(gameData.initialState.G || {}).length > 0) {
            const checkpoint = JSON.parse(JSON.stringify(gameData.initialState));
            currentBgioState = { ...currentBgioState, G: checkpoint.G, ctx: checkpoint.ctx ?? currentBgioState.ctx };
          }
          const initialGameState = structuredClone(currentBgioState.G)
          const mateIDs = gameData.mateIDs || [];
          const reconstructedStates: unknown[] = []
          const divergences: boolean[] = []

          for (const actionInfo of gameData.actions) {
            if (actionInfo.type === 'forceGameState') {
              // This is a platform checkpoint, not a game move. It updates
              // replay state without invoking the game's reducer.
              currentBgioState = {
                ...currentBgioState,
                G: actionInfo.state.G ?? currentBgioState.G,
                ctx: actionInfo.state.ctx ?? currentBgioState.ctx,
              }
              continue
            }
            let playerIndex = mateIDs.indexOf(actionInfo.mateId);
            if (playerIndex === -1 && gameData.mateSlotMap) {
              const slot = gameData.mateSlotMap[actionInfo.mateId];
              if (typeof slot === 'number') {
                playerIndex = slot;
              }
            }
            // Host actions replay under the reserved host identity recorded
            // on the action, never a player seat (see HostPlayerID in @merels/sdk).
            const playerID = actionInfo.asHost ? HOST_PLAYER_ID : playerIndex >= 0 ? String(playerIndex) : '0';

            const bgioAction = {
              type: 'MAKE_MOVE',
              payload: {
                type: actionInfo.move.type,
                args: actionInfo.move.args || [],
                playerID,
                credentials: undefined,
              },
            };

            // Compare against the submitting client's claimed pre-move hash
            // (if present — older, pre-feature actions won't have one) before
            // applying the move, so a mismatch points at exactly which move
            // introduced a divergence. Note this can also fire benignly when
            // two clients submit moves near-simultaneously (see roomActions.ts),
            // so treat it as "worth investigating", not proof of a rules bug.
            const localHash = hashGameState({ G: currentBgioState.G, ctx: currentBgioState.ctx });
            const diverged = actionInfo.stateHash !== undefined && actionInfo.stateHash !== localHash;
            if (diverged) {
              console.warn(
                `[RoomConnection] Possible state divergence before action #${reconstructedStates.length} ` +
                `(${actionInfo.move.type} by ${actionInfo.mateId}): submitter's hash ${actionInfo.stateHash}, locally computed ${localHash}`
              );
            }
            divergences.push(diverged);

            currentBgioState = reducer(currentBgioState, bgioAction as any);
            reconstructedStates.push(structuredClone(currentBgioState.G))
          }

          // A newer broadcast has since triggered its own reconstruction —
          // this one is stale (see reconstructGeneration above). Applying
          // it now would silently revert whatever the newer one already
          // committed, even though this one was TRIGGERED first; it just
          // took longer to resolve (e.g. a slower plugin-version fetch).
          if (myGeneration !== reconstructGeneration) return;

          moveHistory.entries.value = withReconstructedGameStates(
            moveHistory.entries.value,
            reconstructedStates,
            divergences,
          )

          state.activeGame.value = {
            gameId: gameData.gameId,
            gameState: currentBgioState.G,
            ctx: currentBgioState.ctx ?? {},
            playerToMateIds,
            playerIds: gameData.playerIds,
            channel: gameData.channel,
            gameVersion: gameData.gameVersion,
            gameOptions: gameData.gameOptions,
            challengeConfiguration: gameData.challengeConfiguration,
            managed: gameData.managed,
            actions: gameData.actions,
            initialState: { G: initialGameState },
            seed: gameData.seed,
          };
        } catch (error) {
          console.error(`[RoomConnection] Failed to reconstruct ${gameData.gameId} state:`, error);
        }
      };

      reconstruct();
      return;
    }

    console.error(`[RoomConnection] Active game ${gameData.gameId} has no published version`)
  };

  const maybeResyncLocalMates = (serverMates?: Mate[]) => {
    if (!needsMateResync(state.pendingLocalMates, serverMates, clientId)) {
      return;
    }

    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (!proxy || !state.pendingLocalMates) {
      return;
    }

    proxy.updateMates({ clientId, mates: state.pendingLocalMates }).catch((error: unknown) => {
      console.error("[RoomConnection] Failed to resync mates after reconnect", error);
    });
  };

  // Room-level settings in a room snapshot: the state loaded on join and the
  // roomState messages a socket receives, including on reconnecting after
  // changes this client missed. Some roomState messages carry only mates and
  // the active game, so only fields present in the snapshot are applied.
  const applyRoomSettings = (snapshot: object) => {
    const data = snapshot as Record<string, any>;
    batch(() => {
      if (data.roomName) state.roomName.value = data.roomName;
      if (data.ownerName) state.ownerName.value = data.ownerName;
      if (data.imageUrl !== undefined) state.imageUrl.value = data.imageUrl;
      if (data.ownerId) state.ownerId.value = data.ownerId;
      if (data.videoEnabled !== undefined) state.videoEnabled.value = data.videoEnabled;
      if (data.isClosed !== undefined) state.isClosed.value = data.isClosed;
      if (data.opensAt !== undefined) state.opensAt.value = data.opensAt;
      if (data.gameless !== undefined) state.gameless.value = data.gameless;
      if (data.hostMateIds !== undefined) state.hostMateIds.value = data.hostMateIds;
      if (data.managed !== undefined) state.managed.value = data.managed;
      if (data.hostTerm !== undefined) state.hostTerm.value = data.hostTerm;
      if (data.hostPlaysByGameId !== undefined) state.hostPlaysByGameId.value = data.hostPlaysByGameId;
      if (data.curated !== undefined) state.curated.value = data.curated;
      if (data.curatedGameIds !== undefined) state.curatedGameIds.value = data.curatedGameIds;
    });
  };

  // Keeps every game's Host plays in step with the owner's saved options, so
  // game tiles know who may take a seat before any setup starts.
  const trackHostPlays = (gameId: string, savedOptions: GameSavedOptions) => {
    const value = savedOptions?.global?.[HOST_PLAYS_OPTION_ID];
    if (value === undefined) return;
    state.hostPlaysByGameId.value = { ...state.hostPlaysByGameId.value, [gameId]: normalizeHostPlays(value) };
  };

  const handleMessage = (message: RoomBroadcast) => {
    const wasWaitingForRoomToOpen =
      message.type === "roomState" && state.status.value === "error" && state.isClosed.value;
    const serverReportedMates = message.type === "roomState" ? (message.data?.mates as Mate[] | undefined) : undefined;
    batch(() => {
      switch (message.type) {
        case "roomState":
          state.mates.value = message.data.mates || [];
          updateActiveGameFromState(message.data.activeGame);
          if (!message.data.activeGame) {
            // If the server reports no active game, move history should be empty.
            moveHistory.clear()
          }
          break;
        case "gameStarted":
          updateActiveGameFromState(message.data.game);
          state.gameSetup.value = null;
          state.setupReadiness.value = null;
          refreshSavedOptions(message.data.game.gameId);
          refreshGameChannelPreference(message.data.game.gameId);
          refreshChallengeConfigurationPreference(message.data.game.gameId);
          // New game, new move history (for all clients).
          moveHistory.clear()
          break;
        case "gameState":
        case "stateForced":
          updateActiveGameFromState(message.data.game);

          if (message.type === 'stateForced') {
            moveHistory.clear()
            break
          }

          // Record moves broadcast by the Room (cross-client).
          if (message.data?.move && message.data?.mateId) {
            const mateId = String(message.data.mateId)
            const mateName =
              (state.mates.value as any[])?.find((m) => (m as any)?.mateId === mateId)?.name ??
              (state.mates.value as any[])?.find((m) => (m as any)?.mateID === mateId)?.name ??
              undefined

            const moveKey = (() => {
              try {
                return JSON.stringify(message.data.move)
              } catch {
                return null
              }
            })()

            const last = moveHistory.entries.value[moveHistory.entries.value.length - 1]
            const lastKey = (() => {
              try {
                return last ? JSON.stringify(last.move) : null
              } catch {
                return null
              }
            })()

            // If we already have a last entry for this move (e.g. optimistic local submit),
            // update it instead of adding a duplicate.
            if (last && last.mateID === mateId && (moveKey ? lastKey === moveKey : true)) {
              moveHistory.updateLastEntry({
                mateID: mateId,
                mateName,
                move: message.data.move,
                success: true,
                response: message.data,
                sequenceIndex: message.data.seqIndex,
                error: undefined,
              })
            } else {
              moveHistory.addEntry({
                mateID: mateId,
                mateName,
                move: message.data.move,
                success: true,
                response: message.data,
                sequenceIndex: message.data.seqIndex,
              })
            }
          }
          break;
        case "gameVersionUpdated":
          // A new version published to the channel this game is tracking
          // (see @merels/room's GameCatalogClient). Board state/move
          // history is untouched — only the client code changes.
          updateActiveGameFromState(message.data.game);
          break;
        case "gameEnded":
          state.activeGame.value = null;
          moveHistory.clear()
          break;
        case "matesFlushed":
          state.mates.value = [];
          state.activeGame.value = null;
          moveHistory.clear()
          break;
        case "setupStarted":
        case "setupChanged":
          state.gameSetup.value = message.data.setup;
          state.setupReadiness.value = message.data.readiness;
          state.activeGame.value = null;
          moveHistory.clear()
          
          if (message.type === 'setupStarted' && message.data.setup?.gameId) {
             refreshSavedOptions(message.data.setup.gameId);
          }
          break;
        case "setupCancelled":
          state.gameSetup.value = null;
          state.setupReadiness.value = null;
          moveHistory.clear()
          break;
        case "optionsChanged":
          trackHostPlays(message.data.gameId, message.data.savedOptions as GameSavedOptions);
          const currentGameId = state.gameSetup.value?.gameId || state.activeGame.value?.gameId;
          if (currentGameId === message.data.gameId) {
             state.savedOptions.value = message.data.savedOptions as GameSavedOptions;
          }
          break;
        case "gameChannelPreferenceChanged":
          if (state.activeGame.value?.gameId === message.data.gameId) {
            state.gameChannelPreference.value = message.data.channel;
          }
          break;
        case "challengeConfigurationPreferenceChanged":
          if (state.activeGame.value?.gameId === message.data.gameId) {
            state.challengeConfigurationPreference.value = message.data.challengeConfiguration;
          }
          break;
        case "gamelessModeChanged":
          state.gameless.value = message.data.gameless;
          break;
        case "hostingChanged":
          state.hostMateIds.value = message.data.hostMateIds;
          state.managed.value = message.data.managed;
          state.hostTerm.value = message.data.hostTerm;
          break;
        case "curationChanged":
          state.curated.value = message.data.curated;
          state.curatedGameIds.value = message.data.curatedGameIds;
          break;
      }
    });

    if (message.type === "roomState") {
      maybeResyncLocalMates(serverReportedMates);
      applyRoomSettings(message.data);
      // A visitor denied while the room was closed keeps reconnecting its socket.
      // The first open state is the successful completion of that pending join.
      if (wasWaitingForRoomToOpen && (message.data as any)?.isClosed === false) {
        batch(() => {
          state.status.value = "connected";
          state.error.value = null;
          state.isLoading.value = false;
        });
      }
    }
  };

  const joinRoom = async (newRoomId: string) => {
    try {
      batch(() => {
        state.status.value = "connecting";
        state.error.value = null;
        state.isLoading.value = true;
        state.isClosed.value = false;
        state.opensAt.value = null;
      });

      container.roomProxyManager.makeNewRoomProxy(newRoomId, { workerUrl, clientId, env });
      const proxy = container.roomProxyManager.currentRoomProxy.value;

      if (!proxy) {
        throw new Error("Failed to create room proxy");
      }

      proxy.onMessage(handleMessage);

      const [roomState, setupData] = await Promise.all([
        proxy.getRoomState(),
        proxy.getGameSetup()
      ]);
      
      batch(() => {
        state.roomId.value = newRoomId;
        state.mates.value = roomState.mates || [];
        updateActiveGameFromState(roomState.activeGame);
        applyRoomSettings(roomState);

        if (setupData) {
          state.gameSetup.value = setupData.setup;
          state.setupReadiness.value = setupData.readiness;
        } else {
          state.gameSetup.value = null;
          state.setupReadiness.value = null;
        }

        state.status.value = "connected";
        state.isLoading.value = false;
      });

      const gameId = setupData?.setup?.gameId || roomState.activeGame?.gameId;
      if (gameId) {
        refreshSavedOptions(gameId);
        refreshGameChannelPreference(gameId);
        refreshChallengeConfigurationPreference(gameId);
      }

      if (state.pendingLocalMates) {
        await proxy.updateMates({ clientId, mates: state.pendingLocalMates });
      }

    } catch (e: any) {
      const roomClosed = e instanceof RoomClosedError
      batch(() => {
        state.status.value = "error";
        if (roomClosed) state.roomId.value = newRoomId;
        state.error.value = e.message || "Failed to join room";
        state.isLoading.value = false;
        state.isClosed.value = roomClosed;
        state.opensAt.value = roomClosed ? e.opensAt : null;
      });
      console.error("Error joining room:", e);
    }
  };

  const createRoom = async (displayName: string, ownerName?: string): Promise<string> => {
    try {
      batch(() => {
        state.status.value = 'connecting';
        state.error.value = null;
      });

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (container.auth) {
        const token = await container.auth.getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const res = await fetch(`${workerUrl}/api/rooms`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ displayName, ownerName }),
      });

      if (!res.ok) {
        let errorMsg = 'Failed to create room';
        try {
          const errorData = (await res.json()) as any;
          if (errorData?.error) {
            errorMsg = errorData.error;
          }
        } catch {}
        throw new Error(errorMsg);
      }

      const data = (await res.json()) as any;
      if (!data?.success || !data?.room?.roomCode) {
        throw new Error('Invalid server response structure');
      }

      return data.room.roomCode;
    } catch (e: any) {
      console.error('Error creating room:', e);
      batch(() => {
        state.status.value = 'error';
        state.error.value = e.message || 'Failed to create room';
      });
      throw e;
    }
  };

  const deleteRoom = async (roomCode: string): Promise<void> => {
    try {
      const headers: Record<string, string> = {};

      if (container.auth) {
        const token = await container.auth.getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const res = await fetch(`${workerUrl}/api/rooms/${roomCode}`, {
        method: 'DELETE',
        headers,
      });

      if (!res.ok) {
        let errorMsg = 'Failed to delete room';
        try {
          const errorData = (await res.json()) as any;
          if (errorData?.error) {
            errorMsg = errorData.error;
          }
        } catch {}
        throw new Error(errorMsg);
      }
    } catch (e: any) {
      console.error('Error deleting room:', e);
      throw e;
    }
  };

  const updateRoom = async (updates: { displayName?: string; imageUrl?: string; ownerName?: string; videoEnabled?: boolean; isClosed?: boolean; opensAt?: string | null }): Promise<void> => {
    try {
      const roomCode = state.roomId.value;
      if (!roomCode) throw new Error("Not inside a room");

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (container.auth) {
        const token = await container.auth.getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const res = await fetch(`${workerUrl}/api/rooms/${roomCode}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(updates),
      });

      if (!res.ok) {
        let errorMsg = 'Failed to update room';
        try {
          const errorData = (await res.json()) as any;
          if (errorData?.error) {
            errorMsg = errorData.error;
          }
        } catch {}
        throw new Error(errorMsg);
      }

      batch(() => {
        if (updates.displayName !== undefined) {
          state.roomName.value = updates.displayName;
        }
        if (updates.imageUrl !== undefined) {
          state.imageUrl.value = updates.imageUrl;
        }
        if (updates.ownerName !== undefined) {
          state.ownerName.value = updates.ownerName;
        }
        if (updates.videoEnabled !== undefined) {
          state.videoEnabled.value = updates.videoEnabled;
        }
        if (updates.isClosed !== undefined) {
          state.isClosed.value = updates.isClosed;
        }
        if (updates.opensAt !== undefined) {
          state.opensAt.value = updates.opensAt;
        }
      });
    } catch (e: any) {
      console.error('Error updating room:', e);
      throw e;
    }
  };

  const leaveRoom = async () => {
    const proxy = container.roomProxyManager.currentRoomProxy.value;
    if (proxy) {
      try {
        proxy.destroyProxy();
      } catch (e) {
        console.warn("Error destroying proxy:", e);
      }
    }

    container.roomProxyManager.currentRoomProxy.value = null;

    // Clear URL before updating state so auto-join effect won't rejoin.
    // Dispatch popstate so mode deps (isOmniMode, etc.) re-read from the new URL.
    if (typeof window !== "undefined") {
      window.history.pushState(null, "", "/");
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
    
    batch(() => {
      state.status.value = "disconnected";
      state.roomId.value = null;
      state.roomName.value = null;
      state.ownerName.value = null;
      state.imageUrl.value = null;
      state.ownerId.value = null;
      state.videoEnabled.value = true;
      state.isClosed.value = false;
      state.opensAt.value = null;
      state.gameless.value = false;
      state.hostMateIds.value = [];
      state.managed.value = false;
      state.hostTerm.value = DEFAULT_HOST_TERM;
      state.hostPlaysByGameId.value = {};
      state.curated.value = false;
      state.curatedGameIds.value = [];
      state.activeGame.value = null;
      state.mates.value = [];
      state.gameSetup.value = null;
      state.setupReadiness.value = null;
      state.savedOptions.value = null;
      state.gameChannelPreference.value = null;
    });
  };

  const clearError = () => {
    state.error.value = null;
  };

  return {
    status: state.status as Signal<RoomConnectionStatus>,
    roomId: state.roomId as Signal<string | null>,
    error: state.error as Signal<string | null>,
    joinRoom,
    createRoom,
    deleteRoom,
    updateRoom,
    leaveRoom,
    clearError,
    _workerUrl: workerUrl,
  };
}
